import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Paper, TextField, Typography, debounce } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import moment from 'moment-timezone';
import ToolbarDateRangePicker from '../../../../custom-components/Table/ToolbarDateRangePicker';
import SelectUsers from '../../FunctionalApps/schedule-list-view/common/filter-form/SelectUsers';
import getRoute from '../../../../repository/getRoute';
import { isHauler, isProcessor } from '../../../../../utils';
import LocalizationContext from '../../../../localization/LocalizationContext';
import MultiSelectFilter from './MultiSelectFilter';
import { DELIVERY_STATUS_OPTIONS, REPORT_FILTER_OPTIONS } from './constants';

const SEARCH_DEBOUNCE_MS = 400;
const controlSx = { width: { xs: '100%', sm: 220 } };

export const getDefaultFilters = () => {
    const end = moment().endOf('day');
    return { start: end.clone().subtract(6, 'days').startOf('day'), end, haulers: [], processors: [], routes: [], statuses: [], reports: [], search: '' };
};

export const getDateRangeError = ({ start, end }) => {
    if (!start?.isValid() || !end?.isValid()) return 'Enter a valid date range.';
    if (end.isBefore(start, 'day')) return 'End date must be on or after the start date.';
    return null;
};

const userIds = (selection) => selection.filter((option) => option.category === 'Users').map((option) => option.id);

export const toQueryParams = (filters) => ({
    startDate: filters.start.format('YYYY-MM-DD'),
    endDate: filters.end.format('YYYY-MM-DD'),
    haulingId: userIds(filters.haulers),
    processorId: userIds(filters.processors),
    routeId: filters.routes,
    deliveryStatus: filters.statuses,
    report: filters.reports,
});

const useRouteOptions = () =>
    useQuery({
        queryKey: ['delivery-document-routes'],
        queryFn: async () => {
            const routes = await getRoute({ fields: ['id', 'name'], filterDeleted: true });
            if (routes?.err) throw routes.err;
            return routes.map((route) => ({ id: route.id ?? route._id, name: route.name })).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        },
        staleTime: 5 * 60 * 1000,
    });

const DeliveryDocumentFilters = ({ filters, onChange, onReset, onRefresh, disabled, refreshing }) => {
    const localization = useContext(LocalizationContext);
    const { data: routes = [], isInitialLoading: loadingRoutes } = useRouteOptions();
    const [search, setSearch] = useState(filters.search);
    const dateError = getDateRangeError(filters);

    useEffect(() => setSearch(filters.search), [filters.search]);
    const commitSearch = useCallback(
        debounce((value) => onChange({ search: value }), SEARCH_DEBOUNCE_MS),
        [onChange]
    );
    useEffect(() => () => commitSearch.clear(), [commitSearch]);
    const handleReset = () => {
        commitSearch.clear();
        setSearch('');
        onReset();
    };
    const handleSearch = (event) => {
        setSearch(event.target.value);
        commitSearch(event.target.value);
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: { xs: '100%', sm: 280 }, '& > .MuiGrid-item': { mr: 0 } }}>
                    <ToolbarDateRangePicker label="Session dates" startDate={filters.start} endDate={filters.end} handleDateRangeChange={(start, end) => onChange({ start, end })} disabled={disabled} size="small" sx={{ width: '100%' }} />
                </Box>
                <TextField size="small" label="Search LT #" value={search} onChange={handleSearch} disabled={disabled} sx={controlSx} />
                {!isHauler() && (
                    <Box sx={controlSx}>
                        <SelectUsers label={localization.general.hauling_plural} queryParams={{ role: 'transport', subuser: { eq: false } }} value={filters.haulers} onChange={(haulers) => onChange({ haulers })} disabled={disabled} />
                    </Box>
                )}
                {!isProcessor() && (
                    <Box sx={controlSx}>
                        <SelectUsers label={localization.general.processor_plural} queryParams={{ role: 'processor', subuser: { eq: false } }} value={filters.processors} onChange={(processors) => onChange({ processors })} disabled={disabled} />
                    </Box>
                )}
                <MultiSelectFilter label="Routes" options={routes} value={filters.routes} onChange={(routeIds) => onChange({ routes: routeIds })} disabled={disabled} loading={loadingRoutes} sx={controlSx} />
                <MultiSelectFilter label="Delivery status" options={DELIVERY_STATUS_OPTIONS} value={filters.statuses} onChange={(statuses) => onChange({ statuses })} disabled={disabled} sx={controlSx} />
                <MultiSelectFilter label="Document" options={REPORT_FILTER_OPTIONS} value={filters.reports} onChange={(reports) => onChange({ reports })} disabled={disabled} sx={controlSx} />
                <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={disabled || refreshing || !!dateError} sx={{ height: 40 }}>
                    Refresh
                </Button>
                <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset} disabled={disabled} sx={{ height: 40 }}>
                    Reset
                </Button>
            </Box>
            {dateError && (
                <Typography variant="caption" color="error" role="alert" sx={{ display: 'block', mt: 1 }}>
                    {dateError}
                </Typography>
            )}
        </Paper>
    );
};

export default DeliveryDocumentFilters;
